'use client'

import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  PixelDiv,
  PixelFactoryContext,
  PixelLeftPanel,
  PixelRightPanel,
  PixelText,
  PixelBusinessDiagram,
  colors,
  DcFlexBox,
  DcInput,
  DcButton,
  DcDiv,
} from 'pixel-base'
import styled from 'styled-components'
import { resendOtp, signupOtp } from 'utils/api-routes.util'
import { AuthContext } from 'contexts/AuthContext'
import type { IPixelFactory } from '../../../../decl.d'

const FormSection = styled(PixelDiv)`
  width: 100%;
  max-width: 1600px;
  height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px 40px;
`

const Logo = styled.div`
  text-align: center;
  margin: 20px 0;
`

const LogoText = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  background: ${colors.primaryGradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  letter-spacing: 2px;
`

const Title = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  margin: 20px 0;
  color: #1f2937;
  font-size: 25px;
`

const Instructions = styled.div`
  margin-bottom: 20px;
  line-height: 20px;
  font-weight: 400;
  margin-bottom: 25px;
  max-width: 600px;
  text-align: center;

  strong {
    font-weight: 900;
  }
`

const ResendSection = styled(PixelText)`
  line-height: 20px;
  font-weight: 400;
  margin-bottom: 15px;
  max-width: 600px;
  text-align: center;
`

const BackSection = styled(PixelDiv)`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
`

const SignupOtp = () => {
  const navigate = useNavigate()
  const [otpValue, setOtpValue] = React.useState('') // State for OTP value
  const { showSuccess, showFailure, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  const { dispatchLogout } = React.useContext(AuthContext)
  const handleOtpChange = (value: string) => {
    setOtpValue(value)
  }

  const handleOtpComplete = (value: string) => {
    // OTP is complete, you can auto-submit or show success message
  }

  const handleSubmit = (e) => {
    showPageLoader()
    e.preventDefault()
    signupOtp({ otp: otpValue }).subscribe((res: any) => {
      hidePageLoader()
      if (res.response?.data?.success === 1) {
        showSuccess(res.response?.data?.message || 'OTP verified')
        const currentUserInfo = JSON.parse(
          localStorage.getItem('user_info') || '{}',
        )
        const updatedUserInfo = {
          ...currentUserInfo,
          is_otp_verified: true,
        }
        localStorage.setItem('user_info', JSON.stringify(updatedUserInfo))
        window.dispatchEvent(new Event('auth-change'))
        if (res.response?.data?.is_default) {
          navigate('/onboarding')
        } else {
          navigate('/orders')
        }
      } else {
        showFailure(res.response?.data?.message)
      }
    })
  }

  const [timer, setTimer] = React.useState(60)
  const [timerEnabled, setTimerEnabled] = React.useState(false)

  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined
    if (timerEnabled && timer > 0) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1)
      }, 1000)
    } else if (timer === 0) {
      setTimerEnabled(false) // Disable timer when it reaches 0
    }
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [timerEnabled, timer])

  React.useEffect(() => {
    setTimerEnabled(true)
  }, [])

  const handleResendOTP = () => {
    showPageLoader()
    resendOtp().subscribe((res: any) => {
      hidePageLoader()
      if (res.response.message === 'OTP is sent to your email') {
        showSuccess('OTP is sent to your email')
        setTimer(60)
        setTimerEnabled(true)
      } else {
        showFailure('OTP sending Failed')
      }
    })
  }

  const preventBackNavigation = () => {
    window.history.pushState(null, '', window.location.href)
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href)
    }
  }

  React.useEffect(() => {
    preventBackNavigation()
  }, [])

  const disableResend = timerEnabled && timer > 0

  return (
    <>
      <DcFlexBox
        height="100vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor={colors.grayLighter}
      >
        <DcFlexBox
          height="684px"
          width="1270px"
          flexDirection="row-reverse"
          backgroundColor={colors.white}
          borderRadius="15px"
          border={`1px solid ${colors.grayBorder}`}
          boxShadow={`0 1px 0 ${colors.grayBorder}50`}
        >
          <PixelRightPanel width="42%">
            <FormSection>
              <Logo>
                <LogoText>PixelOne</LogoText>
              </Logo>

              <Title>OTP Verification</Title>

              <Instructions>
                Check your Email for the <strong>OTP</strong> and Enter it here
                to continue.
              </Instructions>

              <FormWrapper onSubmit={handleSubmit}>
                <DcInput
                  name="otp"
                  as="otp"
                  value={otpValue}
                  onChange={handleOtpChange}
                  onOtpComplete={handleOtpComplete}
                  otpLength={6}
                  otpGap="10px"
                  otpInputWidth="50px"
                  otpInputHeight="50px"
                  inputSize="medium"
                  variant="normal"
                  autoFocus={true}
                />

                <InputContainer>
                  <DcButton
                    variant="gradient"
                    width="75%"
                    margin="0 50px"
                    type="submit"
                  >
                    Verify
                  </DcButton>
                </InputContainer>
              </FormWrapper>

              <ResendSection>
                <DcDiv textAlign="center">{"Didn't receive OTP? "}</DcDiv>
                <DcButton
                  variant="link"
                  onClick={handleResendOTP}
                  disabled={disableResend}
                  style={{ textDecoration: 'none' }}
                >
                  {disableResend ? (
                    `Resend OTP in ${timer} seconds`
                  ) : (
                    <Link to="/auth/signup-otp">Resend OTP</Link>
                  )}
                </DcButton>
              </ResendSection>

              <BackSection>
                <PixelText
                  textSize="14px"
                  style={{
                    fontWeight: '400',
                    letterSpacing: 'normal',
                    lineHeight: '19.6px',
                    textAlign: 'center',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '3px',

                    // margin: '10px 0px',
                  }}
                  customColor="rgb(77, 101, 117)"
                >
                  Need to verify or change your email?
                  <DcButton
                    padding="0"
                    variant="link"
                    // logOut the user on click
                    onClick={() => {
                      localStorage.clear()
                      sessionStorage.clear()

                      // Dispatch auth change event
                      window.dispatchEvent(new Event('auth-change'))

                      // Navigate to login
                      navigate('/auth/login', { replace: true })

                      // Force reload to clear any remaining state
                      window.location.reload()
                    }}
                    style={{
                      fontSize: '.875rem',
                      margin: 'none !important',
                    }}
                  >
                    Back to Signup.
                  </DcButton>
                </PixelText>
              </BackSection>
            </FormSection>
          </PixelRightPanel>

          <PixelLeftPanel
            borderRadius="15px 0px 0px 15px"
            height="684px"
            width="58%"
            backgroundColor={colors.authBg}
          >
            <PixelBusinessDiagram />
          </PixelLeftPanel>
        </DcFlexBox>
      </DcFlexBox>
    </>
  )
}
const FormWrapper = styled.form`
  max-width: 400px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const InputContainer = styled(PixelDiv)`
  width: 100%;
  height: auto;
  display: grid !important;
  margin-bottom: 15px;
  grid-row-gap: 15px;
`

export default SignupOtp
